import { captureException } from '@sentry/react';
import {
  type QueryClient,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

import {
  type OrderModel,
  orderClient,
  type SearchOrderFilterModel,
  type UpdateOrderModel,
} from '@merchant-clients/order';

import { useToast } from '@frontend/components';
import { useClientQuery } from '@frontend/hooks';
import { tApp, tOrder } from '@frontend/locales';

const fetchOrders = async (filter: SearchOrderFilterModel) => {
  return orderClient.list({
    filter,
  });
};

const applyPreOrderFilter = (
  orders: OrderModel[],
  preOrderFilter: number | undefined,
): OrderModel[] => {
  if (!preOrderFilter) {
    return orders;
  }

  const now = new Date();
  const preOrderDate = new Date(now.getTime() + preOrderFilter * 60000);

  const preOrderFiltered = orders.filter((order) => {
    if (!order.timeRequested) {
      return true;
    }

    return new Date(order.timeRequested) < preOrderDate;
  });

  return preOrderFiltered;
};

// Prefix shared by every orders query of a shop, used to update all of them at once.
export const getOrdersQueryKeyPrefix = (shopId: string) => ['orders', shopId];

// The key includes the filter so that views with different filters (kitchen
// display, orders) don't render each other's cached orders while refetching.
export const getOrdersQueryKey = (
  filter: SearchOrderFilterModel,
  preOrder: number | undefined,
): string[] => [
  ...getOrdersQueryKeyPrefix(filter.shopId),
  `${filter.period.from}:${filter.period.to ?? ''}`,
  (filter.status ?? []).join(','),
  (filter.placeIds ?? []).join(','),
  String(preOrder ?? ''),
];

// The app-wide default keeps unused queries (and persists them) for 7 days. With
// one entry per filter that would pile up, and a status update rewrites every one
// of them, so orders drop out of the cache soon after the view stops using them.
const ORDERS_GC_TIME_MS = 5 * 60 * 1000;

export interface UseOrdersQueryProps {
  filter: SearchOrderFilterModel;
  preOrder: number | undefined;
  onError?(error: Error): void;
}

export const useOrdersQuery = ({
  filter,
  preOrder,
  onError,
}: UseOrdersQueryProps) => {
  return useClientQuery({
    queryKey: getOrdersQueryKey(filter, preOrder),
    enabled: !!filter.shopId,
    async onFetch() {
      const orders = await fetchOrders({
        period: filter.period,
        placeIds: filter.placeIds,
        status: filter.status,
        shopId: filter.shopId,
        orderIds: filter.orderIds,
        verificationIds: filter.verificationIds,
      });

      return applyPreOrderFilter(orders, preOrder);
    },
    onError,
    refetchInterval: 10000,
    gcTime: ORDERS_GC_TIME_MS,
  });
};

const updateCachedOrder = (
  queryClient: QueryClient,
  shopId: string,
  orderId: string,
  updateFn: (existingOrder: OrderModel) => OrderModel,
) => {
  queryClient.setQueriesData<OrderModel[]>(
    { queryKey: getOrdersQueryKeyPrefix(shopId) },
    (oldData) => {
      if (!oldData) {
        return oldData;
      }

      const updatedOrders = oldData.map((order: OrderModel) => {
        if (order.id !== orderId) {
          return order;
        }

        return updateFn(order);
      });

      return updatedOrders;
    },
  );
};

export const performOrderStatusUpdate = async (
  queryClient: QueryClient,
  shopId: string,
  orderId: string,
  status: UpdateOrderModel['status'],
): Promise<OrderModel | undefined> => {
  const existingOrder = queryClient
    .getQueriesData<OrderModel[]>({ queryKey: getOrdersQueryKeyPrefix(shopId) })
    .flatMap(([, orders]) => orders ?? [])
    .find((order) => order.id === orderId);

  if (!existingOrder) {
    captureException(
      new Error(
        `Order with id ${orderId} not found during update of order status, ignoring update`,
      ),
      {
        level: 'warning',
      },
    );

    return undefined;
  }

  updateCachedOrder(queryClient, shopId, orderId, (order) => ({
    ...order,
    loadingState: true,
  }));

  try {
    const updatedOrder = await orderClient.update(orderId, {
      shopId,
      status,
    });

    // The update API is synchronous, so the response already reflects the
    // final order state and can be written straight into the cache.
    updateCachedOrder(queryClient, shopId, orderId, () => updatedOrder);

    return updatedOrder;
  } catch (error) {
    updateCachedOrder(queryClient, shopId, orderId, () => existingOrder);
    throw error;
  }
};

export const useUpdateOrderMutation = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation<
    OrderModel | undefined,
    Error,
    {
      shopId: string;
      orderId: string;
      status: UpdateOrderModel['status'];
    }
  >({
    mutationFn: ({ shopId, orderId, status }) =>
      performOrderStatusUpdate(queryClient, shopId, orderId, status),
    onError(error, { orderId }, _context) {
      showToast({
        header: tOrder.actions.statusUpdateError(),
        body: tApp.errorPages.unexpectedErrorDescription(),
        severity: 'error',
      });
      captureException(error, {
        level: 'error',
        extra: {
          message: `Failed to reject order with id ${orderId}`,
        },
      });
    },
  });
};
